/**
 * pdfChartZoom.js : vue complète des graphiques de la page pendant leur capture.
 *
 * Pourquoi : le PDF capture les canvas affichés (pdfCharts). Un zoom laissé à
 * l'écran se retrouvait dans le PDF, qui doit toujours montrer la période
 * analysée ou la session entière (D240). Chaque graphique zoomé revient à sa
 * vue d'origine sans animation, est capturé, puis retrouve la vue de l'écran
 * sans animation. La capture se fait sous le voile « Génération du PDF » :
 * rien ne bouge de façon visible pour l'utilisateur.
 */
import * as Sentry from '@sentry/react';
import { Chart } from 'chart.js';

/** Instance Chart.js d'un conteneur de graphique, si elle porte le plugin de zoom. */
function zoomableChart(selector) {
  const canvas = document.querySelector(selector)?.querySelector('canvas');
  const chart = canvas ? Chart.getChart(canvas) : null;
  return chart && typeof chart.resetZoom === 'function' && typeof chart.isZoomedOrPanned === 'function'
    ? chart : null;
}

/**
 * Remet à zéro le zoom des graphiques zoomés ou déplacés et rend la fonction
 * qui rétablit leur vue. Un graphique qui ne se laisse pas rétablir garde la
 * vue complète : le zoom est perdu, pas le PDF (signalé à Sentry).
 */
export function resetChartsZoom(selectors) {
  const zoomed = selectors.map(zoomableChart)
    .filter((chart) => chart && chart.isZoomedOrPanned())
    .map((chart) => ({ chart, range: { min: chart.scales.x.min, max: chart.scales.x.max } }));
  zoomed.forEach(({ chart }) => chart.resetZoom('none'));
  return () => zoomed.forEach(({ chart, range }) => {
    try {
      chart.zoomScale('x', range, 'none');
    } catch (err) {
      Sentry.captureException(err, { extra: { stage: 'pdf_chart_zoom_restore' } });
    }
  });
}
